import React, { useState, useEffect, useCallback } from "react";
import { apiService } from "../lib/api";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle } from "./ui/card";
import { Badge } from "./ui/badge";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "./ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "./ui/table";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogDescription,
} from "./ui/dialog";
import { Plus, Loader2, Trash2, Copy, Bot, ShieldCheck, Wrench } from "lucide-react";

const MCP_ENDPOINT = `${process.env.REACT_APP_BACKEND_URL || ""}/mcp`;

const ROLE_ORDER = ["viewer", "contributor", "controller", "manager", "admin"];

export default function MCPSection() {
  const [roles, setRoles] = useState([]);
  const [tools, setTools] = useState([]);
  const [identities, setIdentities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [form, setForm] = useState({ name: "", password: "", role: "viewer" });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [rolesRes, toolsRes, identitiesRes] = await Promise.all([
        apiService.getMcpRoles(),
        apiService.getMcpTools(),
        apiService.getMcpIdentities(),
      ]);
      setRoles(rolesRes.data);
      setTools(toolsRes.data);
      setIdentities(identitiesRes.data);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to load MCP identities");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const handleCreate = async () => {
    if (form.name.trim().length < 2) {
      toast.error("Name must be at least 2 characters");
      return;
    }
    if (form.password.length < 8) {
      toast.error("Password must be at least 8 characters");
      return;
    }
    setSaving(true);
    try {
      await apiService.createMcpIdentity({
        name: form.name.trim(),
        password: form.password,
        role: form.role,
      });
      toast.success("MCP identity created");
      setDialogOpen(false);
      setForm({ name: "", password: "", role: "viewer" });
      load();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to create identity");
    } finally {
      setSaving(false);
    }
  };

  const handleRoleChange = async (id, role) => {
    try {
      await apiService.updateMcpIdentityRole(id, role);
      setIdentities((prev) =>
        prev.map((i) => (i.id === id ? { ...i, role } : i))
      );
      toast.success("Role updated");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to update role");
    }
  };

  const handleRevoke = async (id, name) => {
    if (!window.confirm(`Revoke MCP access for "${name}"? Their tokens stop working immediately.`)) {
      return;
    }
    try {
      await apiService.revokeMcpIdentity(id);
      toast.success("Identity revoked");
      load();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to revoke identity");
    }
  };

  const copyEndpoint = () => {
    navigator.clipboard.writeText(MCP_ENDPOINT);
    toast.success("Endpoint copied");
  };

  return (
    <>
      <div className="mb-6">
        <h2 className="text-xl font-semibold text-slate-800 flex items-center gap-2">
          <Bot className="w-6 h-6 text-indigo-600" />
          MCP Access
        </h2>
        <p className="text-slate-500 mt-1">
          Give AI clients (like Claude) scoped, read/write access to your finance data
          over the Model Context Protocol. Each identity holds one role.
        </p>
      </div>

      <Card className="border-0 shadow-sm mb-6">
        <CardHeader className="pb-3">
          <CardTitle className="text-base">Connection</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <div>
            <Label className="text-slate-600">MCP endpoint</Label>
            <div className="flex items-center gap-2 mt-1">
              <code className="flex-1 px-3 py-2 bg-slate-50 border border-slate-200 rounded-lg text-sm text-slate-700 break-all">
                {MCP_ENDPOINT}
              </code>
              <Button variant="outline" size="sm" onClick={copyEndpoint}>
                <Copy className="w-4 h-4" />
              </Button>
            </div>
          </div>
          <ol className="text-sm text-slate-600 space-y-1 list-decimal list-inside">
            <li>Add the endpoint as a custom connector in your AI client.</li>
            <li>The client opens a login page; sign in with an identity below.</li>
            <li>Access is granted for that identity's role. Revoke here to cut it off.</li>
          </ol>
        </CardContent>
      </Card>

      <Card className="border-0 shadow-sm mb-6">
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle>Identities</CardTitle>
          <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
            <DialogTrigger asChild>
              <Button size="sm" className="bg-indigo-600 hover:bg-indigo-700">
                <Plus className="w-4 h-4 mr-1" />
                Add Identity
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Create MCP Identity</DialogTitle>
                <DialogDescription>
                  A login an AI client authenticates as. The password is shown to no
                  one after creation, so store it securely.
                </DialogDescription>
              </DialogHeader>
              <div className="space-y-4 mt-4">
                <div>
                  <Label htmlFor="mcp-name">Name</Label>
                  <Input
                    id="mcp-name"
                    value={form.name}
                    onChange={(e) => setForm({ ...form, name: e.target.value })}
                    placeholder="claude-finance"
                  />
                </div>
                <div>
                  <Label htmlFor="mcp-password">Password</Label>
                  <Input
                    id="mcp-password"
                    type="password"
                    value={form.password}
                    onChange={(e) => setForm({ ...form, password: e.target.value })}
                    placeholder="At least 8 characters"
                  />
                </div>
                <div>
                  <Label htmlFor="mcp-role">Role</Label>
                  <Select
                    value={form.role}
                    onValueChange={(role) => setForm({ ...form, role })}
                  >
                    <SelectTrigger id="mcp-role">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {ROLE_ORDER.map((r) => (
                        <SelectItem key={r} value={r} className="capitalize">
                          {r}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <Button
                  onClick={handleCreate}
                  disabled={saving}
                  className="w-full bg-indigo-600 hover:bg-indigo-700"
                >
                  {saving ? (
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  ) : (
                    <Plus className="w-4 h-4 mr-2" />
                  )}
                  Create Identity
                </Button>
              </div>
            </DialogContent>
          </Dialog>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex items-center justify-center py-8 text-slate-400">
              <Loader2 className="w-5 h-5 animate-spin" />
            </div>
          ) : identities.length === 0 ? (
            <p className="text-sm text-slate-500 py-6 text-center">
              No MCP identities yet. Add one to let an AI client connect.
            </p>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Name</TableHead>
                  <TableHead>Role</TableHead>
                  <TableHead>Created</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {identities.map((i) => (
                  <TableRow key={i.id}>
                    <TableCell className="font-medium">{i.name}</TableCell>
                    <TableCell>
                      <Select
                        value={i.role}
                        onValueChange={(role) => handleRoleChange(i.id, role)}
                      >
                        <SelectTrigger className="w-36 capitalize">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {ROLE_ORDER.map((r) => (
                            <SelectItem key={r} value={r} className="capitalize">
                              {r}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </TableCell>
                    <TableCell className="text-slate-500 text-sm">
                      {i.created_at ? new Date(i.created_at).toLocaleDateString() : "-"}
                    </TableCell>
                    <TableCell className="text-right">
                      <Button
                        variant="ghost"
                        size="sm"
                        className="h-8 w-8 p-0 text-red-500 hover:text-red-600 hover:bg-red-50"
                        onClick={() => handleRevoke(i.id, i.name)}
                        title="Revoke access"
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      <Card className="border-0 shadow-sm">
        <CardHeader className="pb-3">
          <CardTitle className="text-base flex items-center gap-2">
            <ShieldCheck className="w-5 h-5 text-emerald-600" />
            What each role can do
          </CardTitle>
          <p className="text-sm text-slate-500 mt-1">
            Roles are a ladder: each one inherits everything below it.
          </p>
        </CardHeader>
        <CardContent className="space-y-3">
          {roles.map((r) => (
            <div key={r.role} className="flex items-start gap-3">
              <Badge variant="secondary" className="capitalize mt-0.5 min-w-[90px] justify-center">
                {r.role}
              </Badge>
              <p className="text-sm text-slate-600">{r.description}</p>
            </div>
          ))}
        </CardContent>
      </Card>

      <Card className="border-0 shadow-sm mt-6">
        <CardHeader className="pb-3">
          <CardTitle className="text-base flex items-center gap-2">
            <Wrench className="w-5 h-5 text-indigo-600" />
            Available tools
          </CardTitle>
          <p className="text-sm text-slate-500 mt-1">
            What an AI client can call, grouped by the minimum role it needs. An
            identity can use every tool at its role and below.
          </p>
        </CardHeader>
        <CardContent className="space-y-5">
          {ROLE_ORDER.map((role) => {
            const roleTools = tools.filter((t) => t.min_role === role);
            if (roleTools.length === 0) return null;
            return (
              <div key={role}>
                <div className="flex items-center gap-2 mb-2">
                  <Badge className="capitalize bg-indigo-100 text-indigo-700 hover:bg-indigo-100">
                    {role}
                  </Badge>
                  <span className="text-xs text-slate-400">
                    {roleTools.length} tool{roleTools.length === 1 ? "" : "s"}
                  </span>
                </div>
                <div className="space-y-2 pl-1">
                  {roleTools.map((t) => (
                    <div key={t.name} className="flex flex-col gap-0.5">
                      <code className="text-sm font-medium text-slate-800">
                        {t.name}
                      </code>
                      <p className="text-sm text-slate-500 leading-snug">
                        {t.description}
                      </p>
                    </div>
                  ))}
                </div>
              </div>
            );
          })}
        </CardContent>
      </Card>
    </>
  );
}
